import {
  buildProfileRecipientRules,
  buildSelectedCountLabel,
  organisationRecipientRules,
  parseDelimitedQueryValues,
} from "./EmailAssistantManualQueryContextHelpers";
import type { EmailAssistantQueryContext } from "./EmailAssistantQueryContextTypes";

export const buildOrganisationProfileEmailAssistantQueryContext = (
  searchParams: URLSearchParams,
): EmailAssistantQueryContext | null => {
  const organisationId = searchParams.get("eaOrganisationId")?.trim();
  if (!organisationId) {
    return null;
  }
  const selectedUserIds = parseDelimitedQueryValues(
    searchParams.get("eaSelectedUserIds"),
  );
  const defaultRule =
    selectedUserIds.length > 0 ? "selected_users" : "lead_user";

  return {
    contextSections: [
      {
        title: "Organisation handoff",
        subtitle: "Email & Comms resolves recipients",
        emptyText: "No organisation handoff was provided.",
        items: [
          {
            label: "Selected organisation",
            detail:
              selectedUserIds.length > 0
                ? "One organisation and selected profile users are in context. Email & Comms will resolve only those selected users unless the rule changes."
                : "One organisation is in context. Choose the lead user or all organisation users before review.",
          },
          ...(selectedUserIds.length > 0
            ? [
                {
                  label: "Selected users",
                  detail: `${selectedUserIds.length} selected profile ${
                    selectedUserIds.length === 1 ? "user" : "users"
                  } from the organisation profile.`,
                },
              ]
            : []),
          {
            label: "Recipient choices",
            detail:
              selectedUserIds.length > 0
                ? "Selected users, lead user, and all organisation users are available for this profile handoff."
                : "Lead user and all organisation users are available. Select users from the profile Users tab to open a selected-user handoff.",
          },
          {
            label: "Owner boundary",
            detail:
              "CRM opens the composer. Email & Comms owns recipient resolution, preferences, Activity, audit, and any send job.",
          },
        ],
      },
    ],
    manualJobContext: {
      organisationIds: [organisationId],
      partnerIds: [],
      personIds: [],
      recipientRule: defaultRule,
      sourceKind: "organisation_profile",
      userIds: selectedUserIds,
    },
    recipients:
      selectedUserIds.length > 0
        ? selectedUserIds.map((_, index) => ({
            name: `Selected organisation user ${index + 1}`,
            email: "",
            sourceLabel: "Organisation profile",
          }))
        : [
            {
              name: "Organisation recipients",
              email: "",
              sourceLabel: "Organisation profile",
            },
          ],
    recipientRuleOptions: buildProfileRecipientRules(
      organisationRecipientRules,
      selectedUserIds,
    ),
    recipientRuleValue: defaultRule,
    sourceContext: {
      sourceName: "Organisation profile",
      sourceType: "CRM",
      contextLabel:
        "Opened from a CRM organisation. Choose the recipient rule, review open context, and let Email & Comms resolve the final recipient list safely.",
    },
  };
};

export const buildOrganisationsDashboardEmailAssistantQueryContext = (
  searchParams: URLSearchParams,
  selectedCount: string | null,
): EmailAssistantQueryContext | null => {
  const organisationIds = parseDelimitedQueryValues(
    searchParams.get("eaOrganisationIds"),
  );
  if (organisationIds.length === 0) {
    return null;
  }

  return {
    contextSections: [
      {
        title: "Selected organisations",
        subtitle: buildSelectedCountLabel(
          organisationIds,
          selectedCount,
          "organisation",
          "organisations",
        ),
        emptyText: "No organisation selection was provided.",
        items: [
          {
            label: "Recipient rule",
            detail:
              "Choose lead users or all users. Email & Comms resolves the final recipients server-side.",
          },
          {
            label: "Open alerts",
            detail:
              "Review open organisation and user alerts before approving the manual email job.",
          },
        ],
      },
    ],
    manualJobContext: {
      organisationIds,
      partnerIds: [],
      personIds: [],
      recipientRule: "lead_user",
      sourceKind: "organisations_dashboard",
      userIds: [],
    },
    recipients: organisationIds.map((_, index) => ({
      name: `Selected organisation ${index + 1}`,
      email: "",
      sourceLabel: "Organisations dashboard",
    })),
    recipientRuleOptions: organisationRecipientRules,
    recipientRuleValue: "lead_user",
    sourceContext: {
      sourceName: "Organisations dashboard",
      sourceType: "CRM",
      contextLabel:
        "Opened from selected CRM organisations. Email & Comms resolves recipients, warnings, preferences, and the manual job boundary before any customer delivery.",
    },
  };
};
